import { Stepper } from "@shared/components/Stepper";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import Step1ConsumerSelection from "./Step1ConsumerSelection";
import Step2AccountMetersPlans from "./Step2AccountMetersPlans";
import Step3ReadingsConsumption from "./Step3ReadingsConsumption";
import Step4BillReview from "./Step4BillReview";

export const BillFlow: React.FC = () => {
  const { billId, action = "add" } = useParams();
  const remoteUtilityId = useRemoteUtilityId();
  const storageKey = `bill-flow-progress-${remoteUtilityId}-${
    billId || "new"
  }-${action}`;

  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("Single Bill Generation");
  const stepClickAllowed =
    typeof window !== "undefined" && window.location.hash === "#review-generate"
      ? false
      : true;

  startTracking();
  // Memoize the steps array to prevent infinite re-renders
  const steps = useMemo(
    () => [
      {
        title: "Consumer Selection",
        slug: "consumer-selection",
        component: <Step1ConsumerSelection />,
        optional: false,
        infoTitle: "Consumer Selection Help",
        infoDescription:
          "For a consumer’s first bill, the activation date must fall between the invoice period start date and end date. If generating a single bill, the current invoice period must be after the previous one. Example: Previous: 01/01/2025–30/01/2025 → Current: 01/02/2025–29/02/2025. Select the consumer for whom you want to generate a bill. You can search by account number, name, or other identifiers.",
      },
      {
        title: "Account Overview",
        slug: "account-overview",
        component: <Step2AccountMetersPlans />,
        optional: false,
        infoTitle: "Account Overview Help",
        infoDescription:
          "Review the consumer account details, connected meters, and applicable billing plans before proceeding with bill generation.",
      },
      {
        title: "Readings & Payments",
        slug: "readings-payment",
        component: <Step3ReadingsConsumption />,
        optional: false,
        infoTitle: "Readings & Payments Help",
        infoDescription:
          "Enter the current meter readings and configure payment details. Ensure all readings are accurate before proceeding.",
      },
      {
        title: "Review & Generate",
        slug: "review-generate",
        component: <Step4BillReview />,
        optional: false,
        infoTitle: "Review & Generate Help",
        infoDescription:
          "Review all bill details, calculations, and consumer information before generating the final bill.",
      },
    ],
    [remoteUtilityId, storageKey, billId, action]
  );

  const handleStepChange = (
    stepIndex: number,
    previousStep: number,
    stepInfo: any
  ) => {
    logTimeUntilStep(stepIndex);
    console.log(`Step changed from ${previousStep} to ${stepIndex}`, stepInfo);
    // Additional step change logic if needed
  };

  const handleComplete = (
    allStepData: any,
    completedSteps: number[],
    skippedSteps: number[]
  ) => {
    console.log("Bill flow completed:", {
      allStepData,
      completedSteps,
      skippedSteps,
    });

    stopTracking();

    // Handle completion logic - redirect, show success message, etc.
    // You might want to navigate to a success page or bills list
  };

  const handleStepSave = async (
    stepIndex: number,
    stepData: any,
    isDirty: boolean
  ) => {
    // Optional: Handle step-level saving if you need API persistence
    // For now, storage is handled automatically by stepHelpers
    console.log(`Step ${stepIndex} saved:`, { stepData, isDirty });
  };

  const handleValidation = async (stepIndex: number, stepData: any) => {
    // Optional: Add custom validation logic
    // Return { isValid: boolean, errors?: string[] }
    return { isValid: true };
  };

  return (
    <div className="">
      <div className="mb-4">
        <h1 className="page-title">Generate Bill</h1>
        <p className="page-subtitle">
          Create and issue bills for consumers using this streamlined billing
          flow.
          {billId && ` Editing bill: ${billId}`}
        </p>
      </div>

      <Stepper
        steps={steps}
        onStepChange={handleStepChange}
        onStepSave={handleStepSave}
        enableApiSave={false}
        saveOnStepChange={true}
        trackDirtyState={true}
        autoSave={true}
        persistProgress={true}
        storageKey={storageKey}
        enableRouting={true}
        basePath="/bx/single-bill-generation"
        onValidation={handleValidation}
        onComplete={handleComplete}
        className="mb-8"
      />
    </div>
  );
};

export default BillFlow;
